/**
 * WebVitals component that reports Core Web Vitals through the logging
 * pipeline.
 *
 * Uses the web-vitals library to capture Core Web Vitals and reports them
 * through a `Logger` passed in as a prop.
 *
 * Mount this component once, near the root of the tree — see
 * apps/retells/src/logging/LoggerProvider.tsx.
 */

import { useEffect, useRef } from "react";
import type {
  LCPMetricWithAttribution,
  MetricWithAttribution,
  TTFBMetricWithAttribution,
} from "web-vitals/attribution";
// The attribution build, not the plain one. It costs about 2.2KB gzipped over
// `web-vitals` proper (measured on this resolved 6.2.2: `gzip -9` of the
// published dist bundles is 3339 vs 5576 bytes) and buys the fields a slow page
// load cannot be diagnosed without: TTFB's navigation-timing phases (was it
// DNS, the connection, or the server?) and LCP's element, resource url, and
// load phases. The alternative —
// passing `metric.entries[0]` through raw — ships a whole
// PerformanceNavigationTiming per event to reconstruct the same five
// subtractions on the query side, at several times the payload.
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals/attribution";
import { currentPath, resourceLocation } from "./current-path";
import type { Logger } from "./logger";
import {
  createRouteHistory,
  type RouteHistory,
  trackInteractionRoutes,
} from "./route-history";

export interface WebVitalsProps {
  logger: Logger;
}

/**
 * The `web.vitals` `data` payload for one metric report.
 *
 * `path` is a parameter rather than a `currentPath()` call inside this
 * function, and that is the whole point of the shape — see
 * `createVitalsReporter`.
 *
 * Keys are snake_case, like every other performance payload in this package
 * (`chunk_reason`, `long_task_count`), so one Axiom query style covers all of
 * them. Attribution values are rounded to whole milliseconds: sub-millisecond
 * precision on a network phase is noise, and the digits cost payload against
 * the keepalive budget.
 */
export function buildVitalsPayload(
  metric: MetricWithAttribution,
  path: string | undefined,
): Record<string, unknown> {
  return {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_id: metric.id,
    metric_rating: metric.rating,
    path,
    // 'back-forward-cache' and 'prerender' visits have load timings that mean
    // something entirely different from a cold 'navigate'; pooling them is how
    // a TTFB distribution ends up bimodal for no visible reason.
    navigation_type: metric.navigationType,
    ...attributionFields(metric),
  };
}

/**
 * The per-metric diagnostic fields, for the two metrics whose attribution
 * answers a question the value alone cannot: TTFB (*which* phase was slow) and
 * LCP (*what* element was the paint, and what delayed it).
 *
 * CLS/INP/FCP attribution is deliberately not carried. Those payloads are
 * element selectors and interaction traces — a bigger surface, and this
 * package's consumers have no diagnosis waiting on them today.
 */
function attributionFields(
  metric: MetricWithAttribution,
): Record<string, unknown> {
  if (metric.name === "TTFB") return ttfbFields(metric);
  if (metric.name === "LCP") return lcpFields(metric);
  return {};
}

function ttfbFields(
  metric: TTFBMetricWithAttribution,
): Record<string, unknown> {
  const {
    waitingDuration,
    cacheDuration,
    dnsDuration,
    connectionDuration,
    requestDuration,
  } = metric.attribution;
  return {
    ttfb_waiting_ms: Math.round(waitingDuration),
    ttfb_cache_ms: Math.round(cacheDuration),
    ttfb_dns_ms: Math.round(dnsDuration),
    ttfb_connection_ms: Math.round(connectionDuration),
    ttfb_request_ms: Math.round(requestDuration),
  };
}

function lcpFields(metric: LCPMetricWithAttribution): Record<string, unknown> {
  const {
    target,
    url,
    timeToFirstByte,
    resourceLoadDelay,
    resourceLoadDuration,
    elementRenderDelay,
    lcpEntry,
  } = metric.attribution;
  return {
    // Absent when the LCP element is a text node, which is itself the answer
    // to "is this page image-bound or not". Normalized through
    // `resourceLocation` rather than forwarded: web-vitals reports the
    // resource's full absolute URL including its search string, and §11
    // forbids a raw request URL in a `data` bag.
    lcp_url: resourceLocation(url),
    // web-vitals' `getSelector()` output, built from `tagName`/`id`/`class`.
    // Structural in every app here today, but it IS derived from the DOM: an
    // app that puts a user-derived string in an `id` or a class name would
    // send it here. That assumption is worth re-checking before adding a
    // twelfth consumer, not silently inheriting.
    lcp_element: target,
    lcp_size: lcpEntry?.size,
    lcp_load_time_ms:
      lcpEntry === undefined ? undefined : Math.round(lcpEntry.loadTime),
    lcp_ttfb_ms: Math.round(timeToFirstByte),
    lcp_resource_load_delay_ms: Math.round(resourceLoadDelay),
    lcp_resource_load_duration_ms: Math.round(resourceLoadDuration),
    lcp_element_render_delay_ms: Math.round(elementRenderDelay),
  };
}

/**
 * Build the report callback the web-vitals library gets handed, with the
 * route each metric belongs to resolved from `routes` rather than read when it
 * reports.
 *
 * LCP, INP, and CLS do not report when they happen — they report at page
 * hide, whichever route the visitor has wandered to by then. Reading
 * `location.pathname` inside the callback therefore files every one of them
 * against the *last* route of the visit, and the resulting data is not merely
 * imprecise, it is confidently wrong: in ../retells production `/subscribe`, a
 * route that renders no images at all, carried LCP samples attributed to
 * `peter-pan.png`. Anything downstream that groups vitals by route inherits
 * that error silently, because a wrong pathname looks exactly like a right
 * one.
 *
 * The page-load metrics take the route the history was created on — the
 * route the document was loaded on, whose HTML, images, and server response
 * they measured. INP measures one interaction, which in an SPA may happen on
 * any later client-side route, so it takes the route at that interaction's
 * first Event Timing entry.
 */
export function createVitalsReporter(
  emit: (data: Record<string, unknown>) => void,
  routes: RouteHistory = createRouteHistory(currentPath()),
): (metric: MetricWithAttribution) => void {
  return (metric) => {
    const path =
      metric.name === "INP"
        ? interactionPath(metric.entries, routes)
        : routes.initialPath;
    emit(buildVitalsPayload(metric, path));
  };
}

// An INP with no entries (web-vitals' 8ms stand-in after a bfcache restore or
// soft navigation) names no interaction, so it gets no path rather than a
// guessed one.
function interactionPath(
  entries: readonly PerformanceEventTiming[],
  routes: RouteHistory,
): string | undefined {
  if (entries.length === 0) return undefined;
  const firstStart = Math.min(...entries.map((entry) => entry.startTime));
  return routes.pathAt(firstStart);
}

/**
 * WebVitals reporter component.
 *
 * Reports the following metrics:
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - INP (Interaction to Next Paint)
 * - LCP (Largest Contentful Paint)
 * - TTFB (Time to First Byte)
 *
 * @example
 * ```tsx
 * <WebVitals logger={log} />
 * ```
 */
export function WebVitals({ logger }: WebVitalsProps) {
  const logRef = useRef<Logger>(logger);

  // Guards the registration effect below against Strict Mode's dev-only
  // mount -> cleanup -> mount replay. web-vitals exposes no unsubscribe for
  // onCLS/onFCP/onINP/onLCP/onTTFB, so a second run would add a second live
  // reporter per metric that can never be torn down, and every value would
  // log twice. A ref rather than a module-scope flag (contrast
  // packages/ui/src/hooks/use-install-offer.ts's loadRecordedThisDocument):
  // this component lives in a shared package, so nothing stops a consumer
  // from mounting more than one instance, each wired to its own logger — a
  // module-scope flag would silently starve every instance after the first,
  // where a ref only guards the replay of its own instance's effect and
  // survives that replay because React keeps refs across it.
  const hasRegistered = useRef(false);

  // Keep ref updated without triggering the registration effect
  useEffect(() => {
    logRef.current = logger;
  }, [logger]);

  // Register metrics handlers once on mount
  useEffect(() => {
    if (hasRegistered.current) return;
    hasRegistered.current = true;

    const routes = createRouteHistory(currentPath());
    trackInteractionRoutes(window, routes);
    const report = createVitalsReporter((data) => {
      logRef.current.event("web.vitals", data);
    }, routes);

    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  }, []);

  return null;
}
